// =====================================================================================
// FILE: src/components/ui/ContextMenu.jsx
// Right-click context menu for row actions
// =====================================================================================
import React, { useEffect, useRef, useState } from "react";
import { THEME, FONTS } from "../../constants/theme";

export default function ContextMenu({ 
  x, 
  y, 
  onClose, 
  onCopy, 
  onMove,
  onDelete, 
  locked,
  item,
  categories = [],
}) {
  const menuRef = useRef(null);
  const [showMoveSubmenu, setShowMoveSubmenu] = useState(false);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  // Adjust position if menu would go off screen
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (rect.right > viewportWidth) {
        menuRef.current.style.left = `${x - rect.width}px`;
      }
      if (rect.bottom > viewportHeight) {
        menuRef.current.style.top = `${y - rect.height}px`;
      }
    }
  }, [x, y]);

  const menuStyle = {
    position: "fixed",
    top: y,
    left: x,
    background: THEME.dark,
    borderRadius: THEME.radius,
    boxShadow: "0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)",
    border: "1px solid rgba(255,255,255,0.1)",
    padding: "6px 0",
    minWidth: 180,
    zIndex: 10000,
    fontFamily: FONTS.family,
    animation: "contextMenuFadeIn 0.15s ease",
  };

  const menuItemStyle = (disabled = false, danger = false) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    fontSize: FONTS.sm,
    color: disabled 
      ? "rgba(255,255,255,0.3)" 
      : danger 
        ? "#FF6B6B" 
        : "rgba(255,255,255,0.9)",
    cursor: disabled ? "not-allowed" : "pointer",
    background: "transparent",
    border: "none",
    width: "100%",
    textAlign: "left",
    fontFamily: FONTS.family,
    transition: "background 0.1s ease",
  });

  const handleItemClick = (action, disabled = false) => {
    if (disabled) return;
    action();
    onClose();
  };

  // Filter out current category from move options
  const moveCategories = categories.filter(cat => cat !== item?.category);

  return (
    <>
      <style>{`
        @keyframes contextMenuFadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
      
      <div ref={menuRef} style={menuStyle}>
        {/* Row Name Header */}
        <div style={{
          padding: "8px 16px 10px",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          marginBottom: 6,
        }}>
          <div style={{
            fontSize: FONTS.xs,
            color: "rgba(255,255,255,0.5)",
            marginBottom: 2,
          }}>
            Row Actions
          </div>
          <div style={{
            fontSize: FONTS.sm,
            color: "white",
            fontWeight: FONTS.medium,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 200,
          }}>
            {item?.name || "Untitled"}
          </div>
        </div>

        {/* Copy/Duplicate */}
        <button
          style={menuItemStyle(locked)}
          onClick={() => handleItemClick(onCopy, locked)}
          onMouseEnter={(e) => !locked && (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <span style={{ width: 18, textAlign: "center" }}>📋</span>
          <span>Duplicate</span>
          {locked && <span style={{ marginLeft: "auto", fontSize: 12 }}>🔒</span>}
        </button>

        {/* Move - with submenu */}
        <div 
          style={{ position: "relative" }}
          onMouseEnter={() => setShowMoveSubmenu(true)}
          onMouseLeave={() => setShowMoveSubmenu(false)}
        >
          <button
            style={{
              ...menuItemStyle(locked),
              justifyContent: "space-between",
            }}
            onMouseEnter={(e) => !locked && (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 18, textAlign: "center" }}>📁</span>
              <span>Move to...</span>
            </div>
            <span style={{ opacity: 0.5, fontSize: 12 }}>▶</span>
            {locked && <span style={{ fontSize: 12 }}>🔒</span>}
          </button>

          {/* Move Submenu */}
          {showMoveSubmenu && !locked && moveCategories.length > 0 && (
            <div style={{
              position: "absolute",
              left: "100%",
              top: 0,
              background: THEME.dark,
              borderRadius: THEME.radius,
              boxShadow: "0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)",
              border: "1px solid rgba(255,255,255,0.1)",
              padding: "6px 0",
              minWidth: 180,
              maxHeight: 300,
              overflowY: "auto",
              zIndex: 10001,
            }}>
              <div style={{
                padding: "6px 16px 8px",
                fontSize: FONTS.xs,
                color: "rgba(255,255,255,0.5)",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                marginBottom: 4,
              }}>
                Select category
              </div>
              {moveCategories.map((cat) => (
                <button
                  key={cat}
                  style={menuItemStyle(false)}
                  onClick={() => {
                    onMove(item, cat);
                    onClose();
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <span style={{ width: 18, textAlign: "center" }}>→</span>
                  <span style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 140,
                  }}>
                    {cat}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div style={{
          height: 1,
          background: "rgba(255,255,255,0.1)",
          margin: "6px 0",
        }} />

        {/* Delete */}
        <button
          style={menuItemStyle(locked, true)}
          onClick={() => handleItemClick(onDelete, locked)}
          onMouseEnter={(e) => !locked && (e.currentTarget.style.background = "rgba(255,107,107,0.15)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <span style={{ width: 18, textAlign: "center" }}>🗑️</span>
          <span>Delete</span>
          {locked && <span style={{ marginLeft: "auto", fontSize: 12 }}>🔒</span>}
        </button>
      </div>
    </>
  );
}
